import { Link } from 'react-router-dom';
import AppHeader from '../AppHeader';
import BuyerNav from './BuyerNav';
import { useBuyer } from '../../hooks/useBuyer';

interface Props {
  slug: string;
  title: string;
  /** One sentence on what the list is for; receives the buyer's name. */
  lead: (buyerName: string) => string;
  /** The add button; a screen without one leaves both out. */
  addLabel?: string;
  onAdd?: () => void;
  /** The add button, so a page can put focus back on it after a save. */
  addButtonRef?: React.RefObject<HTMLButtonElement>;
  /** True once the list itself answered 404: the buyer is gone even if the list of buyers still names it. */
  buyerMissing?: boolean;
  children: React.ReactNode;
}

function NotFound({ message }: { message: string }) {
  return (
    <div className="max-w-sm">
      <h1 className="text-display text-ink">Buyer not found</h1>
      <p className="mt-2 text-body text-ink-muted">{message}</p>
      <Link to="/" className="btn-secondary mt-6">
        Back to MerchantView
      </Link>
    </div>
  );
}

function NotLoaded({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="max-w-sm">
      <h1 className="text-display text-ink">Buyer not loaded</h1>
      <p className="mt-2 text-body text-ink-muted">
        We could not load the buyer just now. Check your connection and try again.
      </p>
      <div className="mt-6 flex gap-3">
        <button type="button" className="btn-primary" onClick={onRetry}>
          Try again
        </button>
        <Link to="/" className="btn-secondary">
          Back to MerchantView
        </Link>
      </div>
    </div>
  );
}

/** The frame every reference screen shares: header, buyer tabs, title, lead and the add button. */
export default function ReferenceScreen({ slug, title, lead, addLabel, onAdd, addButtonRef, buyerMissing, children }: Props) {
  const buyer = useBuyer(slug);

  let body: React.ReactNode;
  if (buyerMissing || buyer.status === 'missing') {
    body = <NotFound message="There is no buyer at this address. Pick one from the MerchantView home page." />;
  } else if (buyer.status === 'loading') {
    body = (
      <p className="text-body text-ink-muted" role="status">
        Loading {title.toLowerCase()}
      </p>
    );
  } else if (buyer.status === 'failed') {
    body = <NotLoaded onRetry={buyer.retry} />;
  } else {
    body = (
      <>
        <BuyerNav slug={slug} buyerName={buyer.buyer.name} />
        <div className="mt-8 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-xl">
            <h1 className="text-display text-ink">{title}</h1>
            <p className="mt-2 text-body text-ink-muted">{lead(buyer.buyer.name)}</p>
          </div>
          {addLabel && onAdd && (
            <button type="button" className="btn-primary" onClick={onAdd} ref={addButtonRef}>
              {addLabel}
            </button>
          )}
        </div>
        {children}
      </>
    );
  }

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="mx-auto max-w-5xl px-4 py-12">{body}</main>
    </div>
  );
}
